/**
 * Client Project Detail
 *
 * Vue détaillée d'un projet avec situations et messagerie
 * Design System: Contexte DASHBOARD (text-gray-900, bg-white/70)
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  FileCheck,
  MessageCircle,
  FolderOpen,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useClientProject,
  useProjectSituations,
  useProjectMessages,
  useSendMessage,
  useProjectDocumentStats,
} from '../hooks/useClientPortal';
import type { ClientSituation, ClientMessage, SendMessageInput } from '../types/client-portal.types';
import { SituationValidationModal } from './SituationValidationModal';
import { ProjectDocumentsTab } from './ProjectDocumentsTab';
import { ProjectTimelineTab } from './ProjectTimelineTab';

type TabType = 'situations' | 'messages' | 'documents' | 'timeline';

export function ClientProjectDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: project, isLoading: projectLoading } = useClientProject(id || '');
  const { data: situations, isLoading: situationsLoading } = useProjectSituations(id || '');
  const { data: messages, isLoading: messagesLoading } = useProjectMessages(id || '');
  const { data: documentStats } = useProjectDocumentStats(id || '');
  const sendMessage = useSendMessage();

  const [activeTab, setActiveTab] = useState<TabType>('situations');
  const [selectedSituation, setSelectedSituation] = useState<ClientSituation | null>(null);
  const [newMessage, setNewMessage] = useState('');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-teal-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent >= 25) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  const getSituationStatus = (situation: ClientSituation) => {
    if (situation.validation) {
      switch (situation.validation.action) {
        case 'VALIDATED':
          return { label: t('common.clientPortal.situations.statusValidated'), color: 'bg-green-50 text-green-700', icon: CheckCircle2 };
        case 'REJECTED':
          return { label: t('common.clientPortal.situations.statusRejected'), color: 'bg-red-50 text-red-700', icon: XCircle };
        case 'DISPUTED':
          return { label: t('common.clientPortal.situations.statusDisputed'), color: 'bg-amber-50 text-amber-700', icon: AlertCircle };
      }
    }
    if (situation.submittedAt) {
      return { label: t('common.clientPortal.situations.statusPending'), color: 'bg-blue-50 text-blue-700', icon: Clock };
    }
    return { label: t('common.clientPortal.situations.statusDraft'), color: 'bg-gray-50 text-gray-600', icon: FileCheck };
  };

  const pendingSituations = useMemo(() => {
    return situations?.filter((s) => s.submittedAt && !s.validation) || [];
  }, [situations]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !id) return;

    const input: SendMessageInput = {
      projectId: id,
      content: newMessage.trim(),
    };

    sendMessage.mutate(
      { projectId: id, input },
      {
        onSuccess: () => {
          setNewMessage('');
        },
      }
    );
  };

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('common.clientPortal.projectDetail.notFound')}</p>
        <button
          onClick={() => navigate('/portal/projects')}
          className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
        >
          {t('common.clientPortal.projectDetail.backToProjectsList')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/portal/projects')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t('common.clientPortal.projectDetail.backToProjects')}</span>
      </button>

      {/* Project Header */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              {project.reference && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg">
                  {project.reference}
                </span>
              )}
            </div>
            {(project.address || project.city) && (
              <div className="flex items-center gap-2 text-gray-500 mb-4">
                <MapPin className="w-4 h-4" />
                <span>{[project.address, project.zipCode, project.city].filter(Boolean).join(', ')}</span>
              </div>
            )}
            {project.description && (
              <p className="text-gray-600 max-w-2xl">{project.description}</p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap gap-4">
            {project.startDate && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">{t('common.clientPortal.projectDetail.start')}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(project.startDate)}</p>
                </div>
              </div>
            )}
            {pendingSituations.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl">
                <FileCheck className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-xs text-amber-600">{t('common.clientPortal.projectDetail.toValidate')}</p>
                  <p className="text-sm font-semibold text-amber-700">{pendingSituations.length}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">{t('common.clientPortal.projectDetail.globalProgress')}</span>
            <span className="text-sm font-semibold text-gray-900">{Math.round(project.progressPercent)}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(project.progressPercent)} rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(project.progressPercent, 100)}%` }}
            />
          </div>
          {project.budgetTTC && (
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{t('common.clientPortal.projectDetail.invoicedAmount', { amount: formatCurrency(project.totalInvoiced) })}</span>
              <span>{t('common.clientPortal.projectDetail.budgetAmount', { amount: formatCurrency(project.budgetTTC) })}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('situations')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'situations'
              ? 'text-teal-600 border-teal-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <FileCheck className="w-5 h-5" />
          <span>{t('common.clientPortal.projectDetail.situationsTab')}</span>
          {(situations?.length || 0) > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {situations?.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'messages'
              ? 'text-teal-600 border-teal-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <MessageCircle className="w-5 h-5" />
          <span>{t('common.clientPortal.projectDetail.messagesTab')}</span>
          {project.unreadMessages > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
              {project.unreadMessages}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'documents'
              ? 'text-teal-600 border-teal-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <FolderOpen className="w-5 h-5" />
          <span>{t('common.clientPortal.projectDetail.documentsTab')}</span>
          {documentStats && documentStats.totalDocuments > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {documentStats.totalDocuments}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'timeline'
              ? 'text-teal-600 border-teal-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <Clock className="w-5 h-5" />
          <span>{t('common.clientPortal.projectDetail.timelineTab')}</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'situations' && (
        <SituationsTab
          situations={situations || []}
          isLoading={situationsLoading}
          onSelectSituation={setSelectedSituation}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getSituationStatus={getSituationStatus}
        />
      )}

      {activeTab === 'messages' && (
        <MessagesTab
          messages={messages || []}
          isLoading={messagesLoading}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSendMessage={handleSendMessage}
          isSending={sendMessage.isPending}
          formatDateTime={formatDateTime}
        />
      )}

      {activeTab === 'documents' && id && (
        <ProjectDocumentsTab projectId={id} />
      )}

      {activeTab === 'timeline' && id && (
        <ProjectTimelineTab projectId={id} />
      )}

      {/* Validation Modal */}
      {selectedSituation && id && (
        <SituationValidationModal
          projectId={id}
          situation={selectedSituation}
          onClose={() => setSelectedSituation(null)}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Situations Tab Component
function SituationsTab({
  situations,
  isLoading,
  onSelectSituation,
  formatCurrency,
  formatDate,
  getSituationStatus,
}: {
  situations: ClientSituation[];
  isLoading: boolean;
  onSelectSituation: (situation: ClientSituation) => void;
  formatCurrency: (value: number) => string;
  formatDate: (date: string | null) => string;
  getSituationStatus: (situation: ClientSituation) => { label: string; color: string; icon: React.ElementType };
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (situations.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 border border-white/40 shadow-sm text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <FileCheck className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common.clientPortal.situations.noSituation')}</h3>
        <p className="text-gray-500">{t('common.clientPortal.situations.situationsWillAppear')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {situations.map((situation) => {
        const status = getSituationStatus(situation);
        const StatusIcon = status.icon;
        const isExpanded = expandedId === situation.id;
        const isPending = situation.submittedAt && !situation.validation;

        return (
          <div
            key={situation.id}
            className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm overflow-hidden"
          >
            {/* Header */}
            <div
              className="p-5 cursor-pointer hover:bg-gray-50/50 transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : situation.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-100 rounded-xl">
                    <FileCheck className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">
                        {t('common.clientPortal.situations.situationNumber', { number: situation.situationNumber })}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {status.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {t('common.clientPortal.situations.period', { start: formatDate(situation.periodStart), end: formatDate(situation.periodEnd) })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(situation.periodAmountTTC)}
                    </p>
                    <p className="text-xs text-gray-500">{t('common.clientPortal.situations.ttcPeriod')}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="px-5 pb-5 border-t border-gray-100">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
                  <div>
                    <p className="text-xs text-gray-500">{t('common.clientPortal.situations.previousCumulative')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(situation.previousCumulativeHT)} HT
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('common.clientPortal.situations.currentCumulative')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(situation.currentCumulativeHT)} HT
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('common.clientPortal.situations.progress')}</p>
                    <p className="text-sm font-semibold text-gray-900">{situation.cumulativePercent}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('common.clientPortal.situations.retentionGuarantee')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(situation.retentionAmount)}
                    </p>
                  </div>
                </div>

                {/* Lines */}
                {situation.lines.length > 0 && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('common.clientPortal.situations.workDetail')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-2 text-gray-500 font-medium">{t('common.clientPortal.situations.designation')}</th>
                            <th className="text-right py-2 text-gray-500 font-medium">{t('common.clientPortal.situations.contractHT')}</th>
                            <th className="text-right py-2 text-gray-500 font-medium">{t('common.clientPortal.situations.previous')}</th>
                            <th className="text-right py-2 text-gray-500 font-medium">{t('common.clientPortal.situations.periodColumn')}</th>
                            <th className="text-right py-2 text-gray-500 font-medium">{t('common.clientPortal.situations.cumulPercent')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {situation.lines.map((line) => (
                            <tr key={line.id} className="border-b border-gray-50">
                              <td className="py-2 text-gray-900">
                                {line.code && (
                                  <span className="text-gray-500 mr-2">{line.code}</span>
                                )}
                                {line.description}
                              </td>
                              <td className="py-2 text-right text-gray-600">
                                {formatCurrency(line.contractAmountHT)}
                              </td>
                              <td className="py-2 text-right text-gray-600">
                                {formatCurrency(line.previousAmountHT)} ({line.previousPercent}%)
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">
                                {formatCurrency(line.periodAmountHT)}
                              </td>
                              <td className="py-2 text-right font-semibold text-teal-600">
                                {line.currentPercent}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-3">
                  {isPending && (
                    <button
                      onClick={() => onSelectSituation(situation)}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl
                        shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      {t('common.clientPortal.projectDetail.validateContest')}
                    </button>
                  )}
                  {situation.validation && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl text-sm">
                      <StatusIcon className="w-4 h-4" />
                      <span>{status.label} {t('common.clientPortal.situations.validatedOn', { date: formatDate(situation.validation.validatedAt) })}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Messages Tab Component
function MessagesTab({
  messages,
  isLoading,
  newMessage,
  setNewMessage,
  onSendMessage,
  isSending,
  formatDateTime,
}: {
  messages: ClientMessage[];
  isLoading: boolean;
  newMessage: string;
  setNewMessage: (value: string) => void;
  onSendMessage: () => void;
  isSending: boolean;
  formatDateTime: (date: string) => string;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm overflow-hidden">
      {/* Messages List */}
      <div className="h-96 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
              <MessageCircle className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500">{t('common.clientPortal.messages.noMessages')}</p>
            <p className="text-sm text-gray-400">{t('common.clientPortal.messages.startConversation')}</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isFromClient ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                  message.isFromClient
                    ? 'bg-teal-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
                }`}
              >
                {!message.isFromClient && message.senderName && (
                  <p className="text-xs font-semibold mb-1 text-teal-600">
                    {message.senderName}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message.isFromClient ? 'text-white/70' : 'text-gray-400'
                  }`}
                >
                  {formatDateTime(message.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && onSendMessage()}
            placeholder={t('common.clientPortal.messages.messagePlaceholder')}
            disabled={isSending}
            className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl
              text-gray-900 placeholder-gray-400
              focus:ring-2 focus:ring-teal-500/50 focus:border-transparent
              transition-all duration-200 disabled:opacity-50"
          />
          <button
            onClick={onSendMessage}
            disabled={!newMessage.trim() || isSending}
            className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl
              shadow-lg hover:shadow-xl transition-all duration-300
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              t('common.clientPortal.messages.send')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
